import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ORG_ROLES, orgRoleLabel } from "../auth/orgRoles.ts";
import { roleColor } from "../utils/statusColors.ts";
import DropdownMenu from "./DropdownMenu.tsx";

const LAST_ROLE_HINT = "A member needs at least one role";

/** Roles in the order ORG_ROLES lists them, so chips don't reshuffle on edit. */
function sortRoles(roles: string[]): string[] {
  const order = ORG_ROLES.map((r) => r.value as string);
  return [...roles].sort((a, b) => order.indexOf(a) - order.indexOf(b));
}

function toggle(roles: string[], role: string): string[] {
  return roles.includes(role) ? roles.filter((r) => r !== role) : sortRoles([...roles, role]);
}

/** One role (global or organization) as a soft-tinted chip in its `roleColor`. */
export function RoleChip({ role, label, size = "small" }: { role: string; label: string; size?: "small" | "medium" }) {
  const color = roleColor(role);
  // A soft tint: several roles side by side in every row stay calm.
  const tint =
    color === "default"
      ? {}
      : {
          bgcolor: `rgba(var(--mui-palette-${color}-mainChannel) / 0.12)`,
          color: `${color}.main`,
          fontWeight: 500,
        };
  return <Chip size={size} label={label} sx={tint} />;
}

/** Read-only rendering of an org membership's roles. */
export function OrgRoleChips({ roles, size = "small" }: { roles: string[]; size?: "small" | "medium" }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {sortRoles(roles).map((r) => (
        <RoleChip key={r} role={r} label={orgRoleLabel(r)} size={size} />
      ))}
    </Box>
  );
}

interface OrgRolesMenuProps {
  value: string[];
  /** Called with the new selection on every toggle; never with an empty list. */
  onChange: (roles: string[]) => void;
  disabled?: boolean;
  /** Names the member, for the trigger's accessible label. */
  memberName: string;
}

/**
 * A member's role chips as a menu button: each role toggles in place, with its
 * description. A membership must hold at least one role, so the last one can't
 * be cleared.
 */
export function OrgRolesMenu({ value, onChange, disabled, memberName }: OrgRolesMenuProps) {
  return (
    <DropdownMenu
      label={<OrgRoleChips roles={value} />}
      aria-label={`Roles for ${memberName}`}
      minWidth={320}
      buttonProps={{
        size: "small",
        color: "inherit",
        disabled,
        sx: { ml: -0.75, px: 0.75, minWidth: 0, color: "text.secondary" },
      }}
    >
      {ORG_ROLES.map((r) => {
        const checked = value.includes(r.value);
        const isLast = checked && value.length === 1;
        return (
          <Tooltip key={r.value} title={isLast ? LAST_ROLE_HINT : ""} placement="left">
            <MenuItem
              role="menuitemcheckbox"
              aria-checked={checked}
              aria-disabled={isLast || disabled}
              onClick={(e) => {
                e.stopPropagation();
                if (!isLast && !disabled) onChange(toggle(value, r.value));
              }}
              sx={{ alignItems: "flex-start", whiteSpace: "normal", maxWidth: 360 }}
            >
              <ListItemIcon sx={{ mt: -0.5 }}>
                <Checkbox edge="start" size="small" checked={checked} disabled={isLast} tabIndex={-1} />
              </ListItemIcon>
              <ListItemText primary={r.label} secondary={r.description} />
            </MenuItem>
          </Tooltip>
        );
      })}
    </DropdownMenu>
  );
}

interface OrgRolesChecklistProps {
  value: string[];
  onChange: (roles: string[]) => void;
  disabled?: boolean;
}

/** Every org role as a checkbox with its description, for forms. */
export function OrgRolesChecklist({ value, onChange, disabled }: OrgRolesChecklistProps) {
  return (
    <FormGroup sx={{ gap: 1 }}>
      {ORG_ROLES.map((r) => (
        <FormControlLabel
          key={r.value}
          disabled={disabled}
          control={
            <Checkbox
              size="small"
              checked={value.includes(r.value)}
              onChange={() => onChange(toggle(value, r.value))}
              sx={{ mt: -0.5 }}
            />
          }
          label={
            <>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {r.label}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {r.description}
              </Typography>
            </>
          }
          sx={{ alignItems: "flex-start", m: 0 }}
        />
      ))}
    </FormGroup>
  );
}
